import { z } from "zod";
import { Role, Timestamp } from "./common.ts";

/**
 * GET /api/me — 200 when logged in, 401 ApiError otherwise. Every account has a non-empty name:
 * when a diver signs up without one, the API uses the local part of the email.
 */
export const Me = z
  .object({
    user: z
      .object({ id: z.string(), email: z.email(), name: z.string().min(1).max(120), role: Role })
      .strict(),
  })
  .strict();
export type Me = z.infer<typeof Me>;

export const AdminUser = z
  .object({
    id: z.string(),
    email: z.email(),
    name: z.string().min(1).max(120),
    role: Role,
    banned: z.boolean(),
    createdAt: Timestamp,
    /** Most recent session start; null if the account never signed in. */
    lastSeenAt: Timestamp.nullable(),
    /** Pending suggestions of this user (0 for staff). */
    pendingSuggestions: z.number().int().min(0),
    /**
     * Dive mode granted by an admin (stored flag, default false). Admins have dive mode
     * regardless; for them this only shows the stored flag. Effective access: GET
     * /api/me/features. (Kept out of `Me` so cached older bundles keep parsing /api/me.)
     */
    diveAccess: z.boolean(),
  })
  .strict();
export type AdminUser = z.infer<typeof AdminUser>;

/**
 * GET /api/admin/users?q=&role=&banned=. Newest accounts first. `q` matches name or email
 * (case-insensitive substring). Small community: at most 500 rows, no pagination.
 */
export const AdminUserListQuery = z
  .object({
    q: z.string().trim().min(1).max(120).optional(),
    role: Role.optional(),
    banned: z.enum(["true", "false"]).optional(),
  })
  .strict();
export type AdminUserListQuery = z.infer<typeof AdminUserListQuery>;

export const AdminUserList = z.object({ users: z.array(AdminUser) }).strict();
export type AdminUserList = z.infer<typeof AdminUserList>;

/**
 * POST /api/admin/users — creates STAFF accounts (moderator/admin) with an initial password
 * handed over out-of-band. Divers sign themselves up (better-auth magic link / passkey at
 * /api/auth/*); an admin can later promote a diver via PATCH.
 */
export const AdminUserCreate = z
  .object({
    email: z.email(),
    name: z.string().trim().min(1).max(120),
    role: z.enum(["admin", "moderator"]),
    initialPassword: z.string().min(12).max(128),
  })
  .strict();
export type AdminUserCreate = z.infer<typeof AdminUserCreate>;

/**
 * PATCH /api/admin/users/:id. An admin cannot demote or ban themselves (→ 409 conflict).
 * `diveAccess` grants or revokes dive mode; it takes effect on the user's next request and is
 * audited like any other user change. Demoting an admin keeps the stored flag as it was.
 */
export const AdminUserUpdate = z
  .object({
    name: z.string().trim().min(1).max(120),
    role: Role,
    banned: z.boolean(),
    diveAccess: z.boolean(),
  })
  .partial()
  .strict();
export type AdminUserUpdate = z.infer<typeof AdminUserUpdate>;

/** `:id` param for /api/admin/users/:id — better-auth ids are opaque strings, not UUIDs. */
export const UserIdParam = z.object({ id: z.string().min(1).max(64) }).strict();

/** PATCH /api/me — a user edits their own display name (shown on their suggestions). */
export const MeUpdate = z.object({ name: z.string().trim().min(1).max(120) }).strict();
export type MeUpdate = z.infer<typeof MeUpdate>;
